import React, {PropsWithChildren} from 'react';
import b_ from 'b_';
import {Upload} from 'tus-js-client';

import FileUpload from 'client/common/components/file-upload';
import {FileError, FileRejection} from 'react-dropzone';

import {useCreateResource, useGetVHVideoInfo, useUploadVideo} from 'client/common/hooks/resources';

import {getSortedTranslations} from 'assets/i18n';
import I18N from 'assets/i18n/brandlift-project';

import DnDImage from './images/dnd-image.svg';

import './video-upload.css';

const i18n = I18N.creatives.videoUpload;
const b = b_.with('video-upload');

const getLocalVideoDuration = async (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
        try {
            const video = document.createElement('video');

            video.preload = 'metadata';

            video.onloadedmetadata = function () {
                resolve(video.duration && Math.round(video.duration));

                URL.revokeObjectURL(video.src);
            };
            video.onerror = function (e) {
                reject(new Error(`Error loading video: ${e}`));
            };
            video.src = window.URL.createObjectURL(file);
        } catch (e) {
            reject(e);
        }
    });

const DefaultView = () => {
    return (
        <>
            <div className={b('image-wrapper')}>
                <img src={DnDImage} className={b('image')} />
            </div>
            <div className={b('description')}>
                <p className={b('title')}>{i18n.title}</p>
                <p className={b('details')}>{i18n.details}</p>
                <ul className={b('requirements')}>
                    {getSortedTranslations(i18n.requirements).map((requirement, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index} className={b('requirements-item')}>
                            {requirement}
                        </li>
                    ))}
                </ul>
            </div>
        </>
    );
};

export type VideoUploadProps = PropsWithChildren<{
    className?: string;
    disabled?: boolean;
    minDurationSec: number;
    maxDurationSec: number;
    maxSizeMb: number;
    maxFiles?: number;
    clientId?: string;
    onStart: (tempFileId: string, fileName: File['name']) => void;
    onError: (params: {
        code: string;
        file?: {id: string; name?: string; duration?: number};
    }) => void;
    onProgress: (bytesUploaded: number, bytesTotal: number) => void;
    onSuccess: (tempFileId: string, duration: number, data: {resourceId: string}) => void;
    onUploadInstanceReady: (tempFileId: string, uploadInstance?: Upload) => void;
    onDurationUpdated: (tempFileId: string, timeout: number) => void;
}>;

export default function VideoUpload(props: VideoUploadProps) {
    const {
        className = '',
        disabled,
        onUploadInstanceReady,
        onStart,
        onError,
        onProgress,
        onSuccess,
        onDurationUpdated,
        minDurationSec,
        maxDurationSec,
        maxSizeMb,
        maxFiles,
        children,
        clientId
    } = props;

    const createResource = useCreateResource();
    const uploadVideo = useUploadVideo();
    const getVHVideoInfo = useGetVHVideoInfo();

    async function getVideoDuration(videoMetaId: string, tempFileId: string): Promise<number> {
        const {status, data} = await getVHVideoInfo(videoMetaId);

        if (status === 200 && 'duration' in data) {
            if (data.duration) {
                return data.duration;
            }

            // Ждем пока не появится информация о длительности ролика
            return new Promise(resolve => {
                const timeout = window.setTimeout(() => {
                    resolve(getVideoDuration(videoMetaId, tempFileId));
                }, 10000);

                onDurationUpdated(tempFileId, timeout);
            });
        }

        // eslint-disable-next-line no-cyrillic-string/no-cyrillic-string
        throw new Error('Не удалось получить информацию о загруженном видео');
    }

    async function handleVideoSuccess(
        {videoMetaId, videoFileId}: {videoMetaId: string; videoFileId: string},
        {tempFileId, duration}: {tempFileId: string; duration?: number}
    ) {
        duration = duration || (await getVideoDuration(videoMetaId, tempFileId));

        if (duration < minDurationSec || duration > maxDurationSec) {
            onError({
                code: 'duration-too-long',
                file: {id: tempFileId, duration}
            });

            return;
        }

        const {status, data} = await createResource({
            type: 'vh_video',
            resourceData: {
                videoMetaId: String(videoMetaId),
                videoFileId: String(videoFileId)
            },
            clientId
        });

        if (status === 201 && 'resourceId' in data) {
            onSuccess(tempFileId, duration, data);
        }
    }

    // eslint-disable-next-line max-statements
    async function handleUpload(files: File[]) {
        for (const file of files) {
            const tempFileId = String(Date.now());

            const duration = await getLocalVideoDuration(file);

            if (
                typeof duration === 'number' &&
                (duration < minDurationSec || duration > maxDurationSec)
            ) {
                onError({
                    code: 'duration-too-long',
                    file: {id: tempFileId, name: file.name, duration}
                });
                continue;
            }

            onStart(tempFileId, file.name);

            const uploadInstance = await uploadVideo(file, clientId, {
                onError: () => {
                    onError({code: 'upload-error', file: {id: tempFileId, name: file.name}});
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    onProgress(bytesUploaded, bytesTotal);
                },
                onSuccess: data => {
                    void handleVideoSuccess(data, {tempFileId, duration});
                }
            });

            onUploadInstanceReady(tempFileId, uploadInstance);
        }
    }

    const handleRejected = (rejects: FileRejection[]) => {
        for (const reject of rejects) {
            if (reject.errors.some((error: FileError) => error.code === 'too-many-files')) {
                return onError({code: 'too-many-files'});
            }

            const tempFileId = String(Date.now());

            if (reject.errors.some((error: FileError) => error.code === 'file-too-large')) {
                onError({
                    code: 'file-too-large',
                    file: {id: tempFileId, name: reject.file.name}
                });
            }
        }
    };

    return (
        <FileUpload
            className={`${b({disabled: disabled ? 'yes' : 'no'})} ${className}`}
            accept={['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo']}
            onDropAccepted={handleUpload}
            onDropRejected={handleRejected}
            disabled={disabled}
            maxFiles={maxFiles}
            maxSize={maxSizeMb * 1024 * 1024}
        >
            {children ? children : <DefaultView />}
        </FileUpload>
    );
}
